import { Logout } from '@mui/icons-material';
import { alpha, Avatar, Box, Chip, Stack, Typography, useTheme } from '@mui/material';
import { HttpStatusCode } from 'axios';
import { map, startCase } from 'lodash';
import { Fragment, useState } from 'react';
import { UserPlus } from 'react-feather';
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import FormItemUsers from 'src/components/Form/FormItemUsers';
import PieChart from 'src/components/HighChart/Piechart';
import CustomTable from 'src/components/Table';
import { useNotification } from 'src/hooks/useNotification';
import useTeamMember from 'src/hooks/useTeamMember';
import { useAddTeamMember, useMutationDeleteTeamMember, useMutationLeaveTeam } from 'src/services/mutate';
import {
  useQueryChartsSection,
  useQueryTeamMembers,
  useQueryUsageInformation,
  useQueryUserInfo,
} from 'src/services/queries';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { formatUTCTime } from 'src/utils/helpers/dayjs';
import { ITeamMember } from 'src/utils/typings/response/payment';

const TeamManagement = () => {
  const [params, setParams] = useState({
    offset: 0,
    limit: 10,
  });
  const [selectedUser, setSelectedUser] = useState(
    null as {
      email: string;
      uuid: string;
    } | null,
  );

  const { data: userUsage } = useQueryUsageInformation();
  const { data, isFetching, refetch } = useQueryTeamMembers({
    ...params,
    status: 'active',
    search: '',
    team_id: userUsage?.data?.team_id || 0,
  });
  const { data: user } = useQueryUserInfo();
  const { notification } = useNotification();
  const { mutateAsync: addUser, isPending: addUserPending } = useAddTeamMember();
  const { mutateAsync: leaveTeam, isPending: leaveTeamPending } = useMutationLeaveTeam();
  const { data: chartsSection, isFetching: isFetchingChartsSection } = useQueryChartsSection({
    team_id: userUsage?.data?.team_id || 0,
  });

  const {
    palette: { primary, secondary, error, warning, info, success, text, divider },
    spacing,
  } = useTheme();

  const handleAddMember = async () => {
    if (!selectedUser) return;
    const res = await addUser({
      email: selectedUser.email,
      user_uuid: selectedUser.uuid,
      team_id: userUsage?.data?.team_id || 0,
    });
    if (res.status_code === HttpStatusCode.Created) {
      setSelectedUser(null);
      refetch();
    }
    notification(
      res,
      {
        title: 'Member Added',
        description: `${selectedUser.email} has been added to the team.`,
      },
      {
        title: 'Add Member Failed',
        description: res.data.message || 'There was an issue adding the member to the team.',
      },
    );
  };

  const handleLeaveTeam = async () => {
    const res = await leaveTeam({
      user_uuid: user.data?.uuid || 0,
      team_id: userUsage?.data?.team_id || 0,
    });
    if (res.status_code === HttpStatusCode.Ok) {
      refetch();
    }
    notification(
      res,
      {
        title: 'Left Team',
        description: `You have left the team.`,
      },
      {
        title: 'Leave Team Failed',
        description: res?.data.message || 'There was an issue leaving the team.',
      },
    );
  };

  const configColumn = [
    {
      id: 'member',
      label: 'Member',
      cell: (data: ITeamMember) => (
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar src={data.avatar_url} alt={data.email} />
          <Typography color={'text.secondary'} variant="body2">
            {data.email}
          </Typography>
        </Stack>
      ),
      align: 'left',
      width: 250,
    },
    {
      id: 'created_at',
      label: 'Date Added',
      cell: (data: ITeamMember) => {
        return (
          <Typography color={'text.secondary'} variant="body2">
            {formatUTCTime(FORMAT_DATE_TIME.DD_MM_YYYY_HH_MM_A, data.created_at)}
          </Typography>
        );
      },
      align: 'left',
      width: 150,
    },
    {
      id: 'role',
      label: 'Permission',
      cell: (data: ITeamMember) => (
        <Typography color={'text.secondary'} variant="body2">
          {startCase(data.role)}
        </Typography>
      ),
      align: 'left',
      width: 120,
    },
    {
      id: 'status',
      label: 'Status',
      cell: (data: ITeamMember) => <Chip color="success" label={startCase(data.status)} size="small" />,
      align: 'left',
      width: 120,
    },
    {
      id: 'action',
      label: 'Action',
      cell: (data: ITeamMember) => <ButtonDeleteMember data={data} refetch={refetch} />,
      align: 'center',
      width: 80,
      disableSort: true,
    },
  ];

  const { isOwner } = useTeamMember();

  const renderHeaderActionBar = () => {
    if (!isOwner)
      return (
        <ButtonWithLoading
          isLoading={leaveTeamPending}
          onClick={handleLeaveTeam}
          startIcon={<Logout />}
          variant="outlined"
          color="error"
        >
          Leave Team
        </ButtonWithLoading>
      );
    return (
      <Fragment>
        <FormItemUsers value={selectedUser} sx={{ maxWidth: 400 }} onChange={(_, value) => setSelectedUser(value)} />
        <ButtonWithLoading
          isLoading={addUserPending}
          onClick={handleAddMember}
          disabled={!Boolean(selectedUser)}
          startIcon={<UserPlus size={16} />}
          variant="contained"
        >
          Add Member
        </ButtonWithLoading>
      </Fragment>
    );
  };

  return (
    <WhiteBoxContent display={'flex'} flexDirection={'column'} gap={4}>
      <Stack gap={1} alignItems={'center'} direction={'row'}>
        <Typography flex={1} color={'text.secondary'} variant="h5">
          Team Management
        </Typography>

        {renderHeaderActionBar()}
      </Stack>

      <Stack direction={'row'} gap={spacing(2)} flexWrap={'wrap'}>
        <Box flex={1} border={`1px solid ${divider}`} borderRadius={spacing(2)} p={spacing(2)}>
          <Typography variant="subtitle1">Assigned Credits Distribution</Typography>
          <PieChart
            isLoading={isFetchingChartsSection}
            defaultOptions={{
              series: [
                {
                  type: 'pie',
                  data: [
                    {
                      name: 'Used Credits',
                      y: chartsSection.data.credits_distribution?.used_credits,
                      color: warning.main,
                    },
                    {
                      name: 'Unused Credits',
                      y: chartsSection.data.credits_distribution?.unused_credits,
                      color: warning.light,
                    },
                  ],
                  name: 'Credits',
                  dataLabels: {
                    enabled: true,
                    format: '{point.name}: {point.y} ({point.percentage:.1f}%)',
                    color: text.secondary,
                  },
                },
              ],
            }}
          />
        </Box>

        <Box flex={1} border={`1px solid ${divider}`} borderRadius={spacing(2)} p={spacing(2)}>
          <Typography variant="subtitle1">Total Credits Overview</Typography>
          <PieChart
            isLoading={isFetchingChartsSection}
            defaultOptions={{
              series: [
                {
                  type: 'pie',
                  data: map(chartsSection.data.credits_per_member, (item, i) => ({
                    name: item.email,
                    y: item.credits_used,
                    color: alpha(primary.dark, 1 - (i + 1) * 0.1),
                  })),
                  name: 'Credits',
                  dataLabels: {
                    enabled: true,
                    format: '{point.name}: {point.y} ({point.percentage:.1f}%)',
                    color: text.secondary,
                    style: {
                      textOverflow: 'ellipsis',
                    },
                  },
                },
              ],
            }}
          />
        </Box>
      </Stack>

      <CustomTable
        maxRow={50}
        dataKey={'id'}
        configColumn={configColumn as ConfigCell<ITeamMember>[]}
        data={data.data || []}
        loading={isFetching}
        totalRecords={data.total_records || 0}
        page={params.offset}
        pageSize={params.limit}
        onChangePage={(newPage) => setParams({ ...params, offset: newPage })}
        onChangePageSize={(newPageSize) => setParams({ ...params, limit: newPageSize, offset: 0 })}
      />
    </WhiteBoxContent>
  );
};

export default TeamManagement;

const ButtonDeleteMember = ({ data, refetch }: { data: ITeamMember; refetch: Function }) => {
  const { notification } = useNotification();
  const handleDeleteMember = async (team: ITeamMember) => {
    const res = await mutateAsync({ member_id: team.id, team_id: team.team_id });
    if (res.status_code === HttpStatusCode.Ok) {
      refetch();
    }
    notification(
      res,
      {
        title: 'Member Removed',
        description: `${team.email} has been removed from the team.`,
      },
      {
        title: 'Remove Member Failed',
        description: res.message || 'There was an issue removing the member from the team.',
      },
    );
  };
  const { isPending, mutateAsync } = useMutationDeleteTeamMember();
  const { isOwner } = useTeamMember();

  return (
    <ButtonWithLoading
      disabled={!isOwner}
      onClick={() => handleDeleteMember(data)}
      isLoading={isPending}
      size="small"
      variant="text"
      color="error"
    >
      Remove
    </ButtonWithLoading>
  );
};
